"use client"

import Link from "next/link"
import { UserRound } from "lucide-react"
import { useAuth } from "@/modules/auth"

type HeaderCustomer = NonNullable<ReturnType<typeof useAuth>["customer"]>

function getDisplayName(customer: HeaderCustomer) {
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(" ")
    .trim()
  return name || customer.email || customer.phone || customer.id || "Account"
}

function getInitials(customer: HeaderCustomer) {
  const source = customer.first_name || customer.email || customer.phone || "?"
  return source.trim().charAt(0).toUpperCase() || "?"
}

type HeaderAccountCtaProps = {
  accountHref: string
  loginHref: string
  loginLabel: string
  accountLabel: string
  isAccountLoginActive?: boolean
}

export function HeaderAccountCta({
  accountHref,
  loginHref,
  loginLabel,
  accountLabel,
  isAccountLoginActive = false,
}: HeaderAccountCtaProps) {
  const auth = useAuth()
  const isAuthenticated = auth.status === "authenticated"
  const customer = isAuthenticated ? auth.customer : null

  if (isAuthenticated) {
    // When SSR has pre-resolved status="authenticated" the customer object
    // may still be loading via refresh(). Render the pill anyway with a
    // placeholder avatar so the header doesn't flicker through "Login" first.
    const displayName = customer ? getDisplayName(customer) : accountLabel
    return (
      <div className="header-cta-group" aria-label="Account actions">
        <Link
          href={accountHref}
          className="header-account-pill"
          aria-label={`${accountLabel}: ${displayName}`}
          title={displayName}
        >
          <span className="header-account-avatar" aria-hidden="true">
            {customer ? (
              getInitials(customer)
            ) : (
              <UserRound className="h-4 w-4" aria-hidden="true" />
            )}
          </span>
          <span className="header-account-label">{accountLabel}</span>
        </Link>
      </div>
    )
  }

  const ctaVariantClass = isAccountLoginActive
    ? "header-cta-secondary"
    : "header-cta-primary"

  return (
    <div className="header-cta-group" aria-label="Account actions">
      <Link
        href={loginHref}
        className={`header-cta ${ctaVariantClass}`}
        aria-current={isAccountLoginActive ? "page" : undefined}
      >
        <UserRound className="header-cta-icon" aria-hidden="true" />
        <span>{loginLabel}</span>
      </Link>
    </div>
  )
}
